import { createClient } from '@supabase/supabase-js'
import { Platform } from 'react-native'
import * as SecureStore from 'expo-secure-store'

const SECURE_STORE_CHUNK_SIZE = 1800

function chunkCountKey(key: string) {
  return `${key}.chunk-count`
}

function chunkKey(key: string, index: number) {
  return `${key}.chunk-${index}`
}

async function removeChunks(key: string) {
  const countValue = await SecureStore.getItemAsync(chunkCountKey(key))
  const count = countValue ? Number.parseInt(countValue, 10) : 0

  await SecureStore.deleteItemAsync(chunkCountKey(key))

  if (!Number.isFinite(count) || count <= 0) return

  await Promise.all(
    Array.from({ length: count }, (_, index) => SecureStore.deleteItemAsync(chunkKey(key, index))),
  )
}

const ExpoSecureStoreAdapter = {
  async getItem(key: string) {
    const countValue = await SecureStore.getItemAsync(chunkCountKey(key))
    const count = countValue ? Number.parseInt(countValue, 10) : 0

    if (Number.isFinite(count) && count > 0) {
      const chunks = await Promise.all(
        Array.from({ length: count }, (_, index) => SecureStore.getItemAsync(chunkKey(key, index))),
      )
      return chunks.every((chunk): chunk is string => chunk !== null) ? chunks.join('') : null
    }

    return SecureStore.getItemAsync(key)
  },
  async setItem(key: string, value: string) {
    if (value.length <= SECURE_STORE_CHUNK_SIZE) {
      await removeChunks(key)
      await SecureStore.setItemAsync(key, value)
      return
    }

    const previousCountValue = await SecureStore.getItemAsync(chunkCountKey(key))
    const previousCount = previousCountValue ? Number.parseInt(previousCountValue, 10) : 0
    const chunks = value.match(new RegExp(`.{1,${SECURE_STORE_CHUNK_SIZE}}`, 'g')) ?? []

    await Promise.all(
      chunks.map((chunk, index) => SecureStore.setItemAsync(chunkKey(key, index), chunk)),
    )
    await SecureStore.setItemAsync(chunkCountKey(key), String(chunks.length))
    await SecureStore.deleteItemAsync(key)

    if (Number.isFinite(previousCount) && previousCount > chunks.length) {
      await Promise.all(
        Array.from(
          { length: previousCount - chunks.length },
          (_, index) => SecureStore.deleteItemAsync(chunkKey(key, chunks.length + index)),
        ),
      )
    }
  },
  async removeItem(key: string) {
    await SecureStore.deleteItemAsync(key)
    await removeChunks(key)
  },
}

export const supabase = createClient(
  process.env.EXPO_PUBLIC_SUPABASE_URL!,
  process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY!,
  {
    auth: {
      storage: Platform.OS === 'web' ? undefined : ExpoSecureStoreAdapter,
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: Platform.OS === 'web',
    },
  }
)
